import React, { useEffect, useMemo, useState } from "react";
import { AddFlex } from "../reusableStyles";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Home as HomeIcon,
  Users,
  Zap,
} from "lucide-react";
import {
  INSTAGRAM_CONTACTS,
  INSTAGRAM_CREDITS,
  ONBOARDING_INSTAGRAM_INTEGRATION,
} from "../OnboardingPage/routeNames";
import { getApiCallParams, isNullOrUndefined, logout } from "../utils";
import { usersApiCall, updateApiUserDataAction } from "../Store/userSlice";
import {
  FetchIntegrationsApi,
  FetchUserApi,
  FetchUserFeaturesApi,
} from "../Api/ApiEndPoints";
import { userFeatureApi } from "../Store/UserFeaturesSlice";
import axios from "axios";
import { getBackendBaseURL } from "../axios";
import Loading from "../Loading";
import { setParentLayoutScrollAction } from "../Store/appTempSlice";
import IntegrateInstagramMessage from "./ImportantMessage";
// useUTMTracking was removed in the dead-code cleanup (no analytics backend
// to report to). If UTM tracking is re-added later, restore this import.
import { setInstagramAccountCredentials } from "../Store/instagramSlice.js";
import { getStoreRef } from "../Store/persiststore";
import AppShell from "../components/ui/AppShell";
import Sidebar from "../components/ui/Sidebar";
import Topbar from "../components/ui/Topbar";

// Phase A — The Home component now mounts the new AppShell + Sidebar + Topbar
// primitives. The old LeftSideView/TopBanner/SidebarSection/SidebarNavLink
// files stay on disk (unused) for reference and will be removed in a later
// cleanup pass.

// Map route-ish segments to human-readable breadcrumb labels.
// TODO: replace with a per-page breadcrumb context once screens opt in.
const SEGMENT_LABELS = {
  "": "Home",
  // New canonical slugs
  automations: "Automations",
  leads: "Leads",
  billing: "Billing",
  // Legacy slugs — remain mapped so old bookmarks still show friendly labels
  // during the brief window before the redirect fires.
  instagram_contacts: "Leads",
  instagram_credits: "Billing",
  instagram_feature: "Automations",
  integrations: "Integrations",
  customer_profile: "Customer Profile",
  instagram_integration: "Connect Instagram",
  settings: "Settings",
};

// Segments that are URL namespaces / layout routes — not navigable destinations,
// so we skip them when building breadcrumbs.
const SKIP_BREADCRUMB_SEGMENTS = new Set(["instagram"]);

function humanize(segment) {
  if (SEGMENT_LABELS[segment] != null) return SEGMENT_LABELS[segment];
  return segment
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function deriveBreadcrumbs(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = [{ label: "Home", to: "/" }];
  let acc = "";
  parts.forEach((seg) => {
    acc += `/${seg}`;
    if (SKIP_BREADCRUMB_SEGMENTS.has(seg)) return;
    crumbs.push({ label: humanize(seg), to: acc });
  });
  return crumbs;
}

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instaUsername } = useParams();
  const [searchParams] = useSearchParams();
  const apiUserData = useSelector((state) => state.user.apiUserData);
  const isLoading = useSelector((state) => state.userFeatures.isLoading);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  const instagramIntegrationObj = useSelector((state) => {
    if (isNullOrUndefined(state.user.integrations.integrations)) return false;
    return state.user.integrations.integrations.find(
      (item) => item.id === "instagram_connect"
    );
  });

  /**
   * Auth check on mount:
   * GET /auth/session (cookie sent automatically with withCredentials)
   * → 200: authenticated, load dashboard data
   * → 401: not authenticated, redirect to /login
   */
  useEffect(() => {
    const checkSessionAndLoad = async () => {
      try {
        setLoading(true);
        const sessionRes = await axios.get(
          `${getBackendBaseURL()}auth/session`,
          { withCredentials: true }
        );
        if (!sessionRes.data?.authenticated) {
          window.location.href = "/login";
          return;
        }
        // Seed Redux apiUserData with the profile fields returned by /auth/session.
        // For phone-OTP users the backend sometimes copies the phone into Name
        // (no display name set). Strip that so the Display Name field shows empty
        // (with placeholder) rather than the phone number.
        if (sessionRes.data?.user) {
          const u = sessionRes.data.user;
          const looksLikePhone =
            typeof u.Name === "string" && /^\+?\d[\d\s-]{6,}$/.test(u.Name.trim());
          dispatch(
            updateApiUserDataAction({
              ...u,
              Name:
                looksLikePhone && u.Name.trim() === (u.userNumber || "").trim()
                  ? ""
                  : u.Name,
            })
          );
        }
        // Skip old /instagram_integration onboarding redirect — HomeDashboard handles connect state inline
        dispatch(usersApiCall({ endpoint: FetchUserApi }));
        dispatch(userFeatureApi({ endpoint: FetchUserFeaturesApi }));
        dispatch(usersApiCall(getApiCallParams(FetchIntegrationsApi)));
      } catch (error) {
        if (error.response?.status === 401) {
          window.location.href = "/login";
        } else {
          console.error("Session check failed:", error.message);
          window.location.href = "/login";
        }
      } finally {
        setLoading(false);
      }
    };
    checkSessionAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setParentLayoutScrollAction("scroll"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      instagramIntegrationObj &&
      instagramIntegrationObj.credentials &&
      isNullOrUndefined(instaUsername)
    ) {
      dispatch(
        setInstagramAccountCredentials(instagramIntegrationObj.credentials[0])
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUserData, instagramIntegrationObj]);

  const navSections = useMemo(
    () => [
      {
        label: "INSTAGRAM",
        items: [
          { icon: HomeIcon, label: "Comment Automation", to: "/" },
          { icon: Users, label: "Leads", to: INSTAGRAM_CONTACTS, hidden: true },
          { icon: Zap, label: "Billing", to: INSTAGRAM_CREDITS },
        ],
      },
    ],
    []
  );

  const workspace = useMemo(
    () => ({
      name: "ConvertDM",
      avatarUrl: undefined,
      onClick: () => {
        // TODO: open account/workspace switcher — wired in Phase B
      },
    }),
    []
  );

  const user = useMemo(
    () => ({
      name: (apiUserData?.Name || "").trim() || "User",
      email:
        apiUserData?.email ||
        apiUserData?.Email ||
        apiUserData?.userEmail ||
        apiUserData?.userNumber ||
        "",
      avatarUrl: apiUserData?.profilePicUrl,
      onMenuClick: () => navigate("/settings"),
    }),
    [apiUserData]
  );

  const breadcrumbs = useMemo(() => {
    // TODO: let pages override per-route via a future PageBreadcrumbsContext.
    const tabName = searchParams.get("tabName");
    const base = deriveBreadcrumbs(location.pathname);
    if (tabName && base.length) {
      base[base.length - 1] = { ...base[base.length - 1], label: tabName };
    }
    return base;
  }, [location.pathname, searchParams]);

  if (
    loading ||
    isLoading[FetchUserApi] ||
    isLoading[FetchUserFeaturesApi]
  ) {
    return (
      <AddFlex
        style={{ width: "100vw", height: "100vh" }}
        alignItems="center"
        justify="center"
      >
        <Loading />
      </AddFlex>
    );
  }

  // Top banner removed — the home page renders its own full-page connect prompt
  // when no Instagram account is linked (see HomeDashboard.jsx).
  const showInstagramBanner = false;

  return (
    <AppShell
      sidebar={
        <Sidebar
          navSections={navSections}
          workspace={workspace}
          user={user}
        />
      }
      topbar={
        <Topbar
          breadcrumbs={breadcrumbs}
        />
      }
    >
      {showInstagramBanner && <IntegrateInstagramMessage />}
      <Outlet />
    </AppShell>
  );
}

export default Home;
